import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box, 
  Container, 
  Heading, 
  VStack, 
  Text, 
  Spinner, 
  Center, 
  Divider, 
  useColorModeValue, 
  SimpleGrid,
  HStack,
  Icon,
} from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import { Podcast } from '../types/podcast';
import { Episode } from '../utils/episodes';
import { MdAccessTime } from 'react-icons/md';
import { API_BASE_URL } from '../config/api';

// Define episode card colors
const episodeColors = [
  '#404040',     // Black-75
  '#AA9A6B',     // Gold
  '#5BC5F2',     // Light Blue
  '#CBD7DA',     // Light Gray
  '#BFBFBF',
  '#405080',     // Black-25
];

export const PodcastDetail = () => {
  const { guid } = useParams<{ guid: string }>();
  const [podcast, setPodcast] = useState<Podcast | null>(null);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get color mode values
  const bgColor = useColorModeValue('#F5F5F5', '#1A1A1A');
  const borderColor = useColorModeValue('#E2E2E2', '#333333');  // Lighter border color
  const cardBg = useColorModeValue('white', '#2D2D2D');
  const textColor = useColorModeValue('#404040', '#E5E5E5');
  const headingColor = useColorModeValue('#404040', '#AA9A6B');
  const subheadingColor = useColorModeValue('#666666', '#B5A77D');

  // Calculate days ago from published date
  const getDaysAgo = (dateString: string) => {
    if (!dateString) return '';
    
    const publishedDate = new Date(dateString);
    const today = new Date();
    
    // Reset hours to compare just the dates
    publishedDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    const diffTime = today.getTime() - publishedDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays}d ago`;
  };

  useEffect(() => {
    console.log('Episodes state updated:', episodes);
  }, [episodes]);

  useEffect(() => {
    const fetchPodcastAndEpisodes = async () => {
      if (!guid) return;

      try {
        setIsLoading(true);
        setError(null);

        // Fetch podcast details and episodes
        const response = await fetch(`${API_BASE_URL}/api/podcast/${guid}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        
        console.log('Received podcast data:', data);
        console.log('Sample episode data:', data.episodes?.[0]);
        
        setPodcast(data.podcast);
        setEpisodes(data.episodes || []);
      } catch (error) {
        console.error('Error fetching podcast data:', error);
        setError('Failed to load podcast data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPodcastAndEpisodes();
  }, [guid]);

  // Get episode color based on its position
  const getEpisodeColor = (): string => {
    return useColorModeValue('#E2E8F0', '#2D3748');
  };

  if (isLoading) {
    return (
      <Center py={8}>
        <Spinner size="xl" />
      </Center>
    );
  }

  if (error || !podcast) {
    return (
      <Center py={8}>
        <Text color="red.500">{error || 'Podcast not found'}</Text>
      </Center>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        <Box>
          <VStack align="stretch" spacing={2}>
            <Box>
              <Heading size="xl" color={headingColor}>{podcast.podcast_name}</Heading>
              {podcast.podcaster && (
                <Text color={subheadingColor} fontSize="lg" mt={2}>
                  Hosted by {podcast.podcaster}
                </Text>
              )}
            </Box>
            <HStack spacing={4} color={subheadingColor} fontSize="md">
              <Text>{episodes.length} episodes</Text>
              {episodes.length > 0 && episodes[0].published_date && (
                <Text>• Latest episode {getDaysAgo(episodes[0].published_date)}</Text>
              )}
            </HStack>
          </VStack>
        </Box>

        <Box>
          <Heading size="lg" mb={6} color={headingColor}>Episodes</Heading>
          
          {episodes.length === 0 ? (
            <Text color={textColor} mt={4}>
              No episodes found for this podcast.
            </Text>
          ) : (
            <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={{ base: 3, md: 4, lg: 5 }}>
              {episodes.map((episode, index) => (
                <Link key={episode.episode_id} to={`/episodes/${encodeURIComponent(episode.episode_id)}`}>
                  <Box
                    borderRadius="lg"
                    overflow="hidden"
                    bg={cardBg}
                    boxShadow="0 2px 4px rgba(0,0,0,0.05)"
                    transition="all 0.2s"
                    _hover={{ 
                      transform: 'translateY(-2px)', 
                      boxShadow: 'lg',
                      bg: useColorModeValue('gray.50', 'gray.700')
                    }}
                    borderWidth="1px"
                    borderColor={borderColor}
                    height="100%"
                    display="flex"
                    flexDirection="column"
                  >
                    <Box
                      bg={getEpisodeColor()}
                      p={{ base: 3, md: 4 }}
                      position="relative"
                      display="flex"
                      flexDirection="column"
                      justifyContent="flex-end"
                      minHeight={{ base: "120px", md: "140px" }}
                      _before={{
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.5) 100%)',
                        zIndex: 1,
                      }}
                    >
                      <Text 
                        color="white" 
                        fontSize={{ base: "sm", md: "md" }}
                        fontWeight="semibold"
                        position="relative"
                        zIndex={2}
                        textShadow="0 1px 2px rgba(0,0,0,0.2)"
                        noOfLines={3}
                        lineHeight={{ base: "1.4", md: "1.5" }}
                      >
                        {episode.episode_name || 'Untitled Episode'}
                      </Text>
                    </Box>

                    <Box 
                      p={{ base: 2, md: 3 }} 
                      flex="1"
                      minHeight={{ base: "60px", md: "70px" }}
                    >
                      {episode.published_date && (
                        <HStack spacing={1} color={textColor}>
                          <Icon as={MdAccessTime} fontSize="xs" />
                          <Text fontSize="xs">
                            {getDaysAgo(episode.published_date)}
                          </Text>
                        </HStack>
                      )}
                      {episode.description && (
                        <Text 
                          color={textColor} 
                          fontSize="xs"
                          mt={2} 
                          noOfLines={2}
                          opacity={0.8}
                        >
                          {episode.description}
                        </Text>
                      )}
                    </Box>
                  </Box>
                </Link>
              ))}
            </SimpleGrid>
          )}
        </Box>

        <Divider />

        {podcast.description && (
          <Box
            mt={4}
            p={6}
            borderWidth="1px"
            borderRadius="lg"
            borderColor={borderColor}
            bg={cardBg}
            className="markdown-content"
          >
            <Heading size="lg" mb={4} color={headingColor}>About this Podcast</Heading>
            <ReactMarkdown>{podcast.description}</ReactMarkdown>
          </Box>
        )}
      </VStack>
    </Container>
  );
};